import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useProductNavigation } from '../../../../shared/hooks/useProductNavigation';
import featuredProductService from '../../services/featuredproducts.service';
import { FeaturedProduct } from '../../dto/featuredproducts.dto';
import { useCart } from '../../../../contexts/CartContext';
import { useWishlist } from '../../../wishlist/WishlistContext';
import VariantOptionModalMobile from '../../../../shared/components/VariantOptionModal/VariantOptionModalMobile';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/FeaturedProducts.Mobile.css';
import ProductCardMobile from '../../../../shared/components/ProductCardsA/ProductCardMobile';
import LoadingSpinner from '../../../../shared/components/LodingSpiner/LodingSpiner';

const convertVariantsForDisplay = (product: FeaturedProduct) => {
  if (!product.variants || product.variants.length === 0) {
    return [{
      id: '',
      size: product.title,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      rollLength: 1,
      attributes: {},
      variantImage: undefined,
      variantImages: [],
    }];
  }
  return product.variants.map((variant: any) => {
    let variantDisplayName = variant.variantTitle || variant.sku;
    if (variant.attributes) {
      const attributeValues = Object.values(variant.attributes);
      if (attributeValues.length > 0) {
        variantDisplayName = attributeValues[0] as string;
      }
    }
    const baseVariant = {
      id: variant.sku || variant._id,
      size: variantDisplayName,
      price: variant.price || product.price,
      compareAtPrice: variant.compareAtPrice,
      pricePerMeter: (variant.price || product.price) / (variant.weight || 1),
      rollLength: variant.weight || 90,
      attributes: variant.attributes || {},
      variantImage: variant.variantImages?.[0] || variant.variantImage || undefined,
      variantImages: variant.variantImages || [],
      ...variant
    };
    return baseVariant;
  });
};

const detectVariantHierarchy = (variants: any[]): string[] | undefined => {
  if (!variants || variants.length === 0) return undefined;
  
  const firstVariant = variants[0];
  if (!firstVariant.attributes || typeof firstVariant.attributes !== 'object') {
    return undefined;
  }
  
  if (firstVariant.variantLevels) {
    return Object.keys(firstVariant.variantLevels).map((key: string) => {
      const match = key.match(/level(\d+)/i);
      if (match) {
        return firstVariant.variantLevels[key];
      }
      return firstVariant.variantLevels[key];
    });
  }
  
  const attributeKeys = Object.keys(firstVariant.attributes);
  if (attributeKeys.length > 0 && attributeKeys.length <= 3) {
    return attributeKeys;
  }
  
  return undefined;
};

const FeaturedProducts: React.FC = () => {
  const [products, setProducts] = useState<FeaturedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalProduct, setModalProduct] = useState<FeaturedProduct | null>(null);
  const navigateToProduct = useProductNavigation();
  const dragDistanceRef = useRef<number>(0);

  // Memoize modal data to prevent prop changes on every render
  const modalData = useMemo(() => {
    if (!modalProduct) return null;
    return {
      variants: modalProduct.variants ? convertVariantsForDisplay(modalProduct) : [],
      hierarchicalVariants: modalProduct.variants,
      variantHierarchy: (modalProduct as any).variantHierarchy || detectVariantHierarchy(modalProduct.variants || [])
    };
  }, [modalProduct]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await featuredProductService.getFeaturedProducts();
      if (response && response.success && response.data) {
        setProducts(response.data);
      } else {
        const errorMsg = 'Failed to load featured products';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred while fetching featured products';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const { addToCart } = useCart();
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isItemInWishlist } = useWishlist();
  const lastWishlistActionRef = useRef<number>(0);

  const getTruncatedTitle = (title?: string, maxWords: number = 6) => {
    const safeTitle = title?.trim() || 'Item';
    const words = safeTitle.split(/\s+/);
    if (words.length <= maxWords) return safeTitle;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const handleAddToCart = (product: FeaturedProduct) => {
    try {
      const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
      let selectedVariant = null;
      let variantSku = undefined;
      
      if (hasVariants && Array.isArray(product.variants)) {
        selectedVariant = product.variants.find(v => v.isDefault) || product.variants[0];
        if (!selectedVariant || !selectedVariant.sku || selectedVariant.sku === '') {
          toast.error('No valid variant found for this product.', {
            position: "bottom-right",
            autoClose: 3000,
          });
          return;
        }
        variantSku = selectedVariant.sku;
      }
      
      addToCart(product, 1, variantSku);
    } catch (error) {
    }
  };

  const handleMoreInfo = (product: FeaturedProduct) => {
    // Only navigate if user hasn't dragged (moved less than 10px)
    if (product.slug && dragDistanceRef.current < 10) {
      navigateToProduct(product.slug);
    }
  };

  const handleWishlistToggle = (product: FeaturedProduct) => {
    const now = Date.now();
    if (now - lastWishlistActionRef.current < 300) return;
    lastWishlistActionRef.current = now;

    try {
      const isWishlisted = isItemInWishlist(product._id);
      
      if (isWishlisted) {
        removeFromWishlist(product._id);
        toast.info(`${getTruncatedTitle(product.title)} removed from wishlist`, {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
        let variantId = '';
        let variantSku = '';
        
        if (hasVariants && Array.isArray(product.variants)) {
          const selectedVariant = product.variants.find(v => v.isDefault) || product.variants[0];
          if (!selectedVariant || (!selectedVariant._id && !selectedVariant.sku) || !selectedVariant.sku || selectedVariant.sku === '') {
            toast.error('No valid variant found for this product.', {
              position: "bottom-right",
              autoClose: 3000,
            });
            return;
          }
          variantId = selectedVariant._id || selectedVariant.sku;
          variantSku = selectedVariant.sku;
        }
        
        const productForWishlist = {
          id: product._id,
          _id: product._id,
          title: product.title || 'Unnamed Product',
          slug: product.slug,
          description: product.description || '',
          price: product.price || 0,
          compareAtPrice: product.compareAtPrice ?? null,
          quantity: product.quantity ?? 0,
          images: product.imageUrl ? [product.imageUrl] : [],
          isActive: product.isActive,
          isFeatured: product.isFeatured ?? false,
          hasVariants,
          categories: (product.categories || []).map(c => c.name),
          tags: product.tags || [],
          variantId,
          variantSku,
        };
        
        addToWishlist(productForWishlist, variantSku);
        toast.success(`${getTruncatedTitle(product.title)} added to wishlist`, {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  };



  if (loading) {
    return (
      <div className="featured-products-section-mobile" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="featured-products-section-mobile">
        <div className="error-message-mobile">
          {error}
          <button onClick={fetchFeaturedProducts} className="retry-button-mobile">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="featured-products-section-mobile">
        <div className="featured-products-container-mobile">
          <h2 className="featured-products-title-mobile">Featured Products</h2>
          <div
            className="fp-products-row-mobile"
            style={{
            display: 'flex',
            gap: '1rem',
            padding: '1rem 0',
            overflowX: 'auto',
            flexWrap: 'nowrap',
            maxWidth: '100%',
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {products.map((product) => {
            let imgSrc = product.imageUrl;
            if (!imgSrc) {
              imgSrc = 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
            }
            return (
              <ProductCardMobile
                key={product._id}
                id={product._id}
                title={product.title || 'Unnamed Product'}
                price={`${Math.round(product.price || 0)}`}
                image={imgSrc}
                slug={product.slug || ''}
                deliveryTag={true}
                onAddToCart={() => handleAddToCart(product)}
                onAddToWishlist={() => handleWishlistToggle(product)}
                onViewOptions={() => setModalProduct(product)}
                isWishlisted={isItemInWishlist(product._id)}
                onMoreInfo={() => handleMoreInfo(product)}
                quantity={product.quantity}
                variants={product.variants}
                hierarchicalVariants={product.variants}
                variantHierarchy={(product as any).variantHierarchy}
              />
            );
          })}
          </div>
        </div>
      </div>
      {/* Variant Options Modal - Rendered outside section to avoid stacking context issues */}
      {modalProduct && modalData && (
        <VariantOptionModalMobile
          isOpen={!!modalProduct}
          onClose={() => setModalProduct(null)}
          product={modalProduct}
          variants={modalData.variants}
          hierarchicalVariants={modalData.hierarchicalVariants}
          variantHierarchy={modalData.variantHierarchy}
        />
      )}
    </>
  );
};

export default FeaturedProducts;